import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2, Plus, RefreshCw, Search, ShoppingCart, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { REQUEST_SCOPES, canMakeRequests, dateText, money, procurementError } from "../lib/procurement";
import { ExportButton } from "../components/procurement/ProcurementBits";
import { RequestStatusBadge } from "../components/procurement/RequestBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PAGE_SIZE = 50;
const SCOPE_IDS = REQUEST_SCOPES.map(([id]) => id);

// The queue the reader works from lives in the URL, so a link or a reload lands on the same list.
function useScope() {
  const [params, setParams] = useSearchParams();
  const scope = SCOPE_IDS.includes(params.get("scope")) ? params.get("scope") : "all";
  const search = params.get("q") || "";
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        if (!value || (key === "scope" && value === "all")) next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [{ scope, search }, update];
}

function Due({ request }) {
  if (!request.needed_by) return null;
  return <span className={`text-xs ${request.late ? "text-rose-700 font-medium" : "text-slate-500"}`}>{request.late ? "En retard · " : "Pour le "}{dateText(request.needed_by)}</span>;
}

export default function ProcurementRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [{ scope, search }, update] = useScope();
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0, counts: null, sees_all: false });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);
  const canCreate = canMakeRequests(user);

  const latest = useRef({ search, update });
  latest.current = { search, update };
  useEffect(() => {
    const t = setTimeout(() => {
      const { search: current, update: apply } = latest.current;
      if (searchInput.trim() !== current) apply({ q: searchInput.trim() });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useMemo(() => ({ limit: PAGE_SIZE, scope, ...(search ? { q: search } : {}) }), [scope, search]);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/procurement/requests", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(procurementError(e, "Impossible de charger les demandes d'achat."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/procurement/requests", { params: { ...query, skip: data.items.length } });
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      toast.error(procurementError(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  const tabs = data.sees_all ? REQUEST_SCOPES : [];
  const reset = () => { setSearchInput(""); update({ q: "", scope: "" }); };

  return (
    <div className="space-y-4" data-testid="procurement-requests-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Demandes d'achat</h1>
          <p className="text-sm text-slate-500">
            {data.sees_all ? "De la demande à la dépense : devis, approbation, commande, livraison et facture." : "Vos demandes d'achat, de la demande jusqu'à la livraison."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.sees_all && <ExportButton kind="requests" />}
          {canCreate && <Button asChild data-testid="request-new"><Link to="/achats/demandes/nouvelle"><Plus size={16} className="mr-1.5" />Nouvelle demande</Link></Button>}
        </div>
      </div>

      {tabs.length > 0 && (
        <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0" role="tablist" aria-label="File de travail">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={scope === id} onClick={() => update({ scope: id })} data-testid={`requests-scope-${id}`}
              className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap shrink-0 ${scope === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
              {label}{data.counts?.[id] != null && <span className="ml-1.5 tabular-nums opacity-80">{data.counts[id]}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro ou objet…" className="pl-9 h-9" aria-label="Rechercher une demande" data-testid="requests-search" />
        </div>
        {(search || scope !== "all") && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700" data-testid="requests-error">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="requests-empty">
          <ShoppingCart className="mx-auto text-slate-300" size={26} />
          {search || scope !== "all" ? (
            <><h3 className="font-medium text-slate-900">Aucune demande ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button></>
          ) : (
            <><h3 className="font-medium text-slate-900">Aucune demande d'achat pour le moment</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                {canCreate ? "Besoin d'un ordinateur, d'une licence, de mobilier ? Faites une demande : elle sera approuvée avant toute commande." : "Les demandes d'achat apparaîtront ici."}
              </p>
              {canCreate && <Button asChild size="sm"><Link to="/achats/demandes/nouvelle"><Plus size={14} className="mr-1.5" />Nouvelle demande</Link></Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="requests-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Demande</TableHead><TableHead>Situation</TableHead><TableHead className="hidden lg:table-cell">Demandeur</TableHead>
                  <TableHead className="hidden lg:table-cell">Fournisseur retenu</TableHead><TableHead className="text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/achats/demandes/${r.id}`)} data-testid={`request-row-${r.number}`}>
                    <TableCell>
                      <Link to={`/achats/demandes/${r.id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{r.object}</Link>
                      <div className="text-xs text-slate-500 tabular-nums">{r.number}{r.needed_by ? " · " : ""}<Due request={r} /></div>
                    </TableCell>
                    <TableCell><RequestStatusBadge status={r.status} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{r.requester_name}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{r.supplier_name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-sm tabular-nums text-right">{money(r.quote_minor ?? r.total_minor, r.currency)}{r.quote_minor == null && <div className="text-xs text-slate-400">estimé</div>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="requests-cards">
            {data.items.map((r) => (
              <Link key={r.id} to={`/achats/demandes/${r.id}`} className="flex items-start gap-3 px-4 py-3" data-testid={`request-card-${r.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900 break-words">{r.object}</div>
                  <div className="text-xs text-slate-500 tabular-nums">{r.number} · {r.requester_name}</div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <RequestStatusBadge status={r.status} />
                    <span className="tabular-nums text-slate-700">{money(r.quote_minor ?? r.total_minor, r.currency)}</span>
                  </div>
                  <Due request={r} />
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </Link>
            ))}
          </Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="requests-count">{data.items.length} sur {data.total} demande{data.total > 1 ? "s" : ""}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
